import React from "react";
import toast from "react-hot-toast";

export const SUFFIX_SEP = ":::";
export const TRIP_SEP = "_trip_";

export const GuidanceBlock = ({ guidance }) => {
  if (!guidance || !Array.isArray(guidance) || guidance.length === 0) return null;
  return (
    <div className="mb-3 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
      <p className="text-xs font-semibold text-blue-800 mb-2">💡 Guidance:</p>
      <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
        {guidance.map((item, idx) => (
          <li key={idx} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const evalCondition = (cond, answers, context = {}) => {
  if (!cond) return true;

  const triggerId = cond.triggerQuestionId || cond.triggerFieldId;

  // Logic fix: if this condition is for the parent that is currently repeating, it's satisfied
  if (context.repeatingParentId && triggerId === context.repeatingParentId)
    return true;

  // 1. If passed a Question Object (has displayCondition)
  if (cond.displayCondition) {
    const conditions = cond.displayCondition;
    if (!conditions || conditions.length === 0) return true;

    const logicalOp = (cond.logicalOperator || "AND").toUpperCase();
    const check = (c) => evalCondition(c, answers, context);

    if (logicalOp === "OR") return conditions.some(check);
    return conditions.every(check);
  }

  // 2. If passed an Array of conditions (recursive case)
  if (Array.isArray(cond)) {
    return cond.every((c) => evalCondition(c, answers, context));
  }

  // 3. Single Condition Object
  if (!cond.triggerQuestionId && !cond.triggerFieldId) return true;
  if (!cond.operator) return true;

  const triggerQuestionId = cond.triggerQuestionId || cond.triggerFieldId;
  const { operator, value: expected } = cond;
  let actual;

  // --- HIERARCHICAL LOOKUP ---
  let hasRowDataMatch = false;
  if (context.rowData) {
    const tripSuffixedId = `${triggerQuestionId}${TRIP_SEP}${context.tripIndex}`;
    if (context.tripIndex !== null && context.tripIndex !== undefined && context.rowData.hasOwnProperty(tripSuffixedId)) {
      actual = context.rowData[tripSuffixedId];
      hasRowDataMatch = true;
    } else if (context.rowData.hasOwnProperty(triggerQuestionId)) {
      actual = context.rowData[triggerQuestionId];
      hasRowDataMatch = true;
    }
  }

  if (!hasRowDataMatch) {
    const tripIdx = context.tripIndex;
    let s = context.optionSuffix;

    while (actual === undefined) {
      if (tripIdx !== null && s) {
        actual = answers[`${triggerQuestionId}${TRIP_SEP}${tripIdx}_${s}`];
      }
      if (actual === undefined && s) {
        actual = answers[`${triggerQuestionId}_${s}`];
      }
      if (actual === undefined && tripIdx !== null) {
        actual = answers[`${triggerQuestionId}${TRIP_SEP}${tripIdx}`];
      }
      if (actual === undefined) {
        actual = answers[triggerQuestionId];
      }

      if (actual !== undefined || !s || !s.includes(SUFFIX_SEP)) break;
      s = s.substring(0, s.lastIndexOf(SUFFIX_SEP));
    }
  }

  if (operator === "exists") {
    let exists;
    if (Array.isArray(actual)) {
      exists = actual.length > 0;
    } else if (
      typeof actual === "number" ||
      (!isNaN(actual) && actual !== "")
    ) {
      exists = parseFloat(actual) > 0;
    } else {
      exists = actual !== undefined && actual !== null && actual !== "";
    }
    return expected ? exists : !exists;
  }

  // Standard comparisons
  if (Array.isArray(actual)) {
    if (operator === "eq") return actual.includes(expected);
    if (operator === "ne") {
      if (actual.length === 0) return false;
      return actual.some((val) => val !== expected);
    }
  }

  const actualVal = actual === undefined || actual === null ? "" : actual;
  if (operator === "eq") return actualVal == expected;
  if (operator === "ne") {
    if (
      actualVal === "" ||
      (Array.isArray(actualVal) && actualVal.length === 0)
    )
      return false;
    return actualVal != expected;
  }

  return true;
};

export const getMultiselectDependency = (
  field,
  answers,
  questionsList,
  tripIndex = null,
  optionSuffix = null,
) => {
  if (!field.displayCondition) return null;

  const multiselectDep = field.displayCondition.find((cond) => {
    if (cond.operator !== "exists") return false;
    const triggerField = questionsList.find(
      (q) => q.questionId === cond.triggerQuestionId,
    );
    return (
      triggerField?.questionType === "multiselect" ||
      triggerField?.fieldType === "multiselect"
    );
  });

  if (!multiselectDep) return null;

  const multiselectFieldId = multiselectDep.triggerQuestionId;

  // --- HIERARCHICAL LOOKUP ---
  let selectedItems;
  if (answers[multiselectFieldId]) {
    selectedItems = answers[multiselectFieldId];
  }

  if (selectedItems === undefined) {
    let s = optionSuffix;
    while (selectedItems === undefined) {
      if (tripIndex !== null && s)
        selectedItems = answers[`${multiselectFieldId}${TRIP_SEP}${tripIndex}_${s}`];
      if (selectedItems === undefined && s)
        selectedItems = answers[`${multiselectFieldId}_${s}`];
      if (selectedItems === undefined && tripIndex !== null)
        selectedItems = answers[`${multiselectFieldId}${TRIP_SEP}${tripIndex}`];
      if (selectedItems === undefined)
        selectedItems = answers[multiselectFieldId];

      if (selectedItems !== undefined || !s || !s.includes(SUFFIX_SEP)) break;
      s = s.substring(0, s.lastIndexOf(SUFFIX_SEP));
    }
  }

  if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
    return null;
  }

  return multiselectFieldId;
};

export const validateInputValue = (question, rawValue) => {
  if (rawValue === "") return rawValue;

  const questionType = question?.questionType;
  const questionText = String(question?.questionText || "").toLowerCase();
  const isPercentage =
    questionType === "percentage" || questionText.includes("percentage");

  const numericValue = Number(rawValue);

  if (numericValue < 0) {
    toast.error("Negative values are not allowed");
    return "0";
  }

  if (isPercentage && numericValue > 100) {
    toast.error("Percentage cannot exceed 100%");
    return "100";
  }

  const limit = Array.isArray(question?.limit) ? question.limit[0] : null;
  if (limit && typeof limit === "string" && limit.includes("-")) {
    const [min, max] = limit.split("-").map(Number);

    if (!Number.isNaN(min) && numericValue < min) {
      toast.error(`Value cannot be less than ${min}`);
      return String(min);
    }
    if (!Number.isNaN(max) && numericValue > max) {
      toast.error(`Value cannot exceed ${max}`);
      return String(max);
    }
  }

  return rawValue;
};